// ============================================
// Bảng danh sách góp ý
// ============================================
// Dùng `DataTable` chung (loading skeleton + empty state đã có sẵn ở đó).
//
// Quy ước đọc FK của payload: UUID nằm ở `store`/`product`/`order`, tên hiển thị
// ở `store_name`/`product_name` PHẲNG bên cạnh. ĐÚNG BA FK đó (+ `handled_by`)
// là `on_delete=SET_NULL` nên CÓ THỂ cùng `null` — ô Store dưới đây phải chịu
// được ca đó. `customer` thì KHÁC: `on_delete=CASCADE`, không nullable, luôn có
// tên (xem `StaffFeedback` trong shared-types).

import { useMemo } from 'react';
import type { StaffFeedback } from '@conai/shared-types';
import { DataTable, StatusBadge, type Column } from '../../../components/shared';
import { EMPTY_VALUE, FEEDBACK_STATUS_VARIANT, excerpt, useFeedbackText } from '../feedbackText';
import { RatingStars } from './RatingStars';

interface FeedbackTableProps {
  items: StaffFeedback[];
  isLoading: boolean;
  onSelect: (row: StaffFeedback) => void;
}

function PhotoIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <circle cx="10" cy="10" r="2.75" />
    </svg>
  );
}

export function FeedbackTable({ items, isLoading, onSelect }: FeedbackTableProps) {
  const { t, tr, statusLabel, typeLabel, issueKindLabel, formatDate, formatTime } = useFeedbackText();

  const columns = useMemo<Column<StaffFeedback>[]>(
    () => [
      {
        key: 'created_at',
        header: tr('feedbackColumnDate', 'Received'),
        width: '132px',
        render: (row) => (
          <div className="whitespace-nowrap">
            <p className="font-medium text-slate-900">{formatDate(row.created_at)}</p>
            <p className="text-xs text-slate-400">{formatTime(row.created_at)}</p>
          </div>
        ),
      },
      {
        key: 'customer_name',
        header: t('customer'),
        // `customer_name` LUÔN có giá trị: FK `customer` là CASCADE + không
        // nullable, tên lấy từ `full_name` bắt buộc, và khách "bị xoá" thực chất
        // là soft delete nên bản ghi vẫn còn nguyên. Nhánh "Deleted account" cũ
        // là code chết dựng trên một giả định BE đã bác bỏ (28/08/2026).
        render: (row) => <span className="font-medium text-slate-900">{row.customer_name}</span>,
      },
      {
        key: 'store_name',
        header: tr('feedbackColumnStore', 'Store'),
        render: (row) => <span className="text-slate-600">{row.store_name || EMPTY_VALUE}</span>,
      },
      {
        key: 'feedback_type',
        header: t('type'),
        render: (row) => (
          <div className="whitespace-nowrap">
            <p className="text-slate-700">{typeLabel(row.feedback_type)}</p>
            {/* `issue_kind` chỉ khác `null` khi loại là PRODUCT_ISSUE — không cần
                kiểm tra loại ở đây, chỉ cần kiểm tra chính nó. */}
            {row.issue_kind ? (
              <p className="text-xs text-slate-400">{issueKindLabel(row.issue_kind)}</p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'rating',
        header: tr('feedbackRating', 'Rating'),
        width: '110px',
        render: (row) => <RatingStars rating={row.rating} />,
      },
      {
        key: 'comment',
        header: tr('feedbackColumnComment', 'Comment'),
        /**
         * Bug #247 — góp ý dài làm chữ đè lên nhau giữa các dòng.
         *
         * Ô này trước đây chỉ có `max-w-[320px]` đặt trên một `<span>` nằm
         * trong `<td>` không khai `width`. Bảng chạy `table-layout: auto`, nên
         * bề rộng cột do NỘI DUNG quyết định chứ không do max-width của span:
         * một góp ý dài kéo cột phình ra, đẩy các cột khác chồng lên nhau, và
         * một chuỗi liền không dấu cách (link, mã đơn khách dán vào) thì tràn
         * thẳng ra ngoài ô vì không có điểm ngắt dòng nào.
         *
         * Ba thứ cùng lúc mới đủ: `width` cố định cho cột, `break-words` để
         * chuỗi liền có chỗ ngắt, `line-clamp-2` để ô luôn cao đúng hai dòng
         * bất kể nội dung. Nội dung đầy đủ vẫn đọc được qua `title` và ở modal
         * chi tiết.
         */
        width: '300px',
        render: (row) => {
          const text = excerpt(row.comment);
          const full = (row.comment ?? '').trim();
          return text ? (
            <span
              className="line-clamp-2 block max-w-[300px] break-words align-top text-slate-600"
              title={full || undefined}
            >
              {text}
            </span>
          ) : (
            <span className="italic text-slate-400">{tr('feedbackNoComment', 'No comment')}</span>
          );
        },
      },
      {
        key: 'photo_urls',
        header: tr('feedbackColumnPhotos', 'Photos'),
        align: 'center',
        width: '90px',
        render: (row) =>
          // `photo_urls` LUÔN là mảng (rỗng chứ không null) — không cần `?? []`.
          row.photo_urls.length > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
              <PhotoIcon />
              {row.photo_urls.length}
            </span>
          ) : (
            <span className="text-slate-300">{EMPTY_VALUE}</span>
          ),
      },
      {
        key: 'status',
        header: t('status'),
        width: '140px',
        render: (row) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge variant={FEEDBACK_STATUS_VARIANT[row.status] ?? 'neutral'}>
              {statusLabel(row.status)}
            </StatusBadge>
            {/* Dấu "khách đã sửa lại góp ý sau khi cửa hàng xử lý"
                (`staff_content_stale`). CỐ Ý chỉ là một chấm nhỏ, không phải
                badge chữ: cả lời giải thích nằm trong banner ở modal chi tiết,
                ở đây chỉ cần đủ để người ta biết dòng nào phải mở lại. Một cột
                cảnh báo to trong bảng sẽ át cột Status — mà Status mới là thứ
                người dùng quét mắt theo.
                BE chưa deploy thì field này `undefined` -> falsy -> không vẽ gì,
                đúng như mong muốn. Không cần `?? false`. */}
            {row.staff_content_stale ? (
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full bg-amber-500"
                title={tr('feedbackStaleTitle', 'The customer edited this feedback after it was handled')}
                aria-label={tr('feedbackStaleTitle', 'The customer edited this feedback after it was handled')}
              />
            ) : null}
          </div>
        ),
      },
    ],
    [t, tr, statusLabel, typeLabel, issueKindLabel, formatDate, formatTime]
  );

  return (
    <DataTable
      columns={columns}
      data={items}
      rowKey="id"
      showFilter={false}
      showHeaderMenu={false}
      isLoading={isLoading}
      onRowClick={onSelect}
      emptyMessage={tr('feedbackEmpty', 'No feedback matches these filters.')}
    />
  );
}
